import { Fragment, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router';
import { useFormFactor } from '@/lib/formFactor';
import { useSession } from '@/stores/session';
import { Icon } from '@/components/Icon';
import { Empty } from '@/components/States';
import { ViewSidebar, type SidebarView } from '@/components/ViewSidebar';
import { ApiError, withQuery } from '@/api/client';
import { money, qty } from '@/lib/format';
import { AppliedChips, CardList, FilterButton, ListBody, ListScaffold, Pager, SearchBox, Segments, ViewChips } from './kit/List';
import { FilterSheet } from './kit/Sheet';
import { DataTable, Two, type Col } from './kit/Table';
import { useInfinite, usePagedList, useResource, useUrlState, withParam } from './kit/hooks';
import { BundleCard } from './Cards';
import { BundleDetail } from './BundleDetail';
import { useCatalogSegments } from './nav';
import { bundleFits, isGroup } from './rules';
import { deleteBundle } from './api';
import type { Bundle, BundleStats, ProductStats } from './types';
import s from './BundlesPage.module.css';

/** 视图 = 老接口 status 参数；老接口不传时默认只看启用，老页面默认传 all（src/app/(app)/bundles/page.tsx） */
const VIEWS: { key: string; label: string; count: (st: BundleStats) => number }[] = [
  { key: 'all', label: '全部套装', count: (st) => st.all },
  { key: 'active', label: '启用', count: (st) => st.active },
  { key: 'inactive', label: '停用', count: (st) => st.inactive },
];

const SIZE: Record<string, { label: string; count: (st: BundleStats) => number }> = {
  single: { label: '单产品套装', count: (st) => st.single },
  multi: { label: '多产品套装', count: (st) => st.multi },
  large: { label: '三项及以上', count: (st) => st.large },
};
const DESC: Record<string, { label: string; count: (st: BundleStats) => number }> = {
  withDescription: { label: '有说明', count: (st) => st.withDescription },
  withoutDescription: { label: '无说明', count: (st) => st.withoutDescription },
};

function exportBundles(rows: Bundle[]) {
  const cell = (value: unknown) => `"${String(value ?? '').replaceAll('"', '""')}"`;
  const lines = [
    ['套装名称', '套装编码', '组件数', '组件构成', '可配齐套数', '套装总成本', '尺寸', '状态'],
    ...rows.map((b) => [
      b.name,
      b.code,
      b.items.length,
      b.items.map((i) => `${i.product.name} × ${qty(i.quantity)}${isGroup(i) ? ` / ${qty(i.setsPerGroup)} 套一组` : ' / 套'}`).join('；'),
      bundleFits(b)[0]?.sets ?? '',
      b.totalCost ?? '',
      b.dimensionText ?? '',
      b.isActive ? '启用' : '停用',
    ]),
  ].map((row) => row.map(cell).join(',')).join('\r\n');
  const url = URL.createObjectURL(new Blob([`\uFEFF${lines}`], { type: 'text/csv;charset=utf-8' }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = '产品套装.csv';
  anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function BundlesPage() {
  const can = useSession((st) => st.can);
  if (!can('products.read')) {
    return can('gallery.read') ? <Navigate to="/gallery" replace /> : <Empty icon="lock" title="没有查看产品套装的权限">需要「产品资料 · 查看」权限，请联系管理员。</Empty>;
  }
  return <BundleList />;
}

function BundleList() {
  const ff = useFormFactor();
  const nav = useNavigate();
  const can = useSession((st) => st.can);
  const desk = ff === 'desktop';
  const segments = useCatalogSegments();
  const [sp, set] = useUrlState();
  const view = VIEWS.find((v) => v.key === sp.get('view')) || VIEWS[0];
  const size = sp.get('size') || undefined;
  const desc = sp.get('desc') || undefined;
  const q = sp.get('q') || '';
  const selectedId = sp.get('id') || undefined;
  const facet = sp.get('facet') || 'all';
  const productStats = useResource<{ stats: ProductStats }>(desk ? withQuery('/api/products', { pageSize: 1, includeStats: 1 }) : null);
  const [panel, setPanel] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Bundle | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const [fitSort, setFitSort] = useState<'asc' | 'desc' | null>(null);

  const query = useMemo(() => ({ status: view.key, size, description: desc, keyword: q || undefined, includeStocks: '1' }), [view, size, desc, q]);
  const list = usePagedList<Bundle, BundleStats>('/api/bundles', query, desk ? 100 : 20, !desk);
  useInfinite(sentinel, !desk, list.loadMore, list.items.length);
  const stats = list.stats;
  const fits = useMemo(() => new Map(list.items.map((b) => [b.id, bundleFits(b)[0]])), [list.items]);
  const facetCounts = useMemo(() => ({
    shortage: list.items.filter((b) => (fits.get(b.id)?.sets ?? 0) < 50).length,
    perGroup: list.items.filter((b) => b.items.some(isGroup)).length,
  }), [list.items, fits]);
  const displayItems = useMemo(() => {
    if (facet === 'all') return list.items;
    if (facet === 'shortage') return list.items.filter((b) => (fits.get(b.id)?.sets ?? 0) < 50);
    if (facet === 'perGroup') return list.items.filter((b) => b.items.some(isGroup));
    return list.items;
  }, [facet, list.items, fits]);
  const mobileItems = useMemo(() => {
    if (!fitSort) return displayItems;
    const direction = fitSort === 'asc' ? 1 : -1;
    return [...displayItems].sort((a, b) =>
      direction * ((fits.get(a.id)?.sets ?? 0) - (fits.get(b.id)?.sets ?? 0)) || a.name.localeCompare(b.name, 'zh-CN'),
    );
  }, [displayItems, fitSort, fits]);

  const cols: Col<Bundle>[] = [
    { key: 'name', title: '套装', w: 190, fix: true, render: (b) => <Two top={<b>{b.name}</b>} sub={b.code} /> },
    { key: 'n', title: '组件数', w: 110, align: 'r', render: (b) => b.items.length },
    {
      key: 'items', title: '组件构成', w: 300, render: (b) => {
        const regularItems = b.items.filter((i) => !isGroup(i));
        const groupItems = b.items.filter(isGroup);
        return <Two
          top={<span className={s.uses}>{regularItems.map((i, k) => <Fragment key={i.id}>{k > 0 && ' · '}{i.product.name}×{qty(i.quantity)}</Fragment>)}</span>}
          sub={<span className={groupItems.length ? s.grp : undefined}>{groupItems.length ? groupItems.map((i, k) => <Fragment key={i.id}>{k > 0 && ' · '}{i.product.name}：每 {qty(i.setsPerGroup)} 套用 {qty(i.quantity)} 个</Fragment>) : '全部按每套用量'}</span>}
        />;
      },
    },
    {
      key: 'fit', title: '可配齐套数', w: 112, align: 'r', render: (b) => {
        const f = fits.get(b.id);
        if (!f) return <span className="em">—</span>;
        return <Two top={<b className={f.sets < 50 ? 'neg' : ''}>{qty(f.sets)}</b>} sub={f.limit ? `卡在${f.limit.product.name}` : undefined} end />;
      },
    },
    { key: 'cost', title: '套装总成本', w: 124, align: 'r', render: (b) => (b.totalCost != null ? money(b.totalCost) : '—') },
    { key: 'dim', title: '尺寸', w: 130, render: (b) => b.dimensionText || <span className="em">—</span> },
    { key: 'actions', title: '操作', w: 120, fixRight: true, render: (b) => <div className={s.rowActions}>
      <button type="button" title={`查看${b.name}`} aria-label={`查看${b.name}`} onClick={(event) => { event.stopPropagation(); nav(`/bundles/${b.id}`); }}><Icon name="eye" size={16} /></button>
      {can('products.bundle.write') && <>
        <button type="button" title={`复制${b.name}`} aria-label={`复制${b.name}`} onClick={(event) => { event.stopPropagation(); nav(`/bundles/${b.id}/copy`); }}><Icon name="plus" size={16} /></button>
        <button type="button" className={s.dangerAction} title={`删除${b.name}`} aria-label={`删除${b.name}`} onClick={(event) => { event.stopPropagation(); setDeleteError(''); setDeleteTarget(b); }}><Icon name="trash" size={16} /></button>
      </>}
    </div> },
  ];

  const sidebarViews: SidebarView[] = [
    { key: 'allProducts', label: '全部产品', icon: 'box', count: productStats.data?.stats.all, to: '/products' },
    { key: 'gallery', label: '产品画册', icon: 'book', to: '/gallery' },
    { key: 'bundles', label: '产品套装', icon: 'dash', count: stats?.all, to: '/bundles' },
    { key: 'shortage', label: '含缺货组件', icon: 'alert', count: list.hasNext ? undefined : facetCounts.shortage, hot: true, separator: true },
    { key: 'perGroup', label: '含每组用量', icon: 'set', count: list.hasNext ? undefined : facetCounts.perGroup },
  ];

  const filtered = !!(q || size || desc || facet !== 'all');
  const empty = (
    <Empty icon="box" title={filtered ? '没有符合条件的套装' : `「${view.label}」里没有套装`}>
      {filtered ? '换个关键词，或者清空筛选再看看。' : '套装在老版新建，下单时按用量规则展开成产品行。'}
    </Empty>
  );
  const common = { state: { loading: list.loading, error: list.error, count: displayItems.length }, errorTitle: '套装列表加载失败', detail: 'GET /api/bundles', onRetry: list.reload, empty };
  const deskCommon = { ...common, state: { ...common.state, count: displayItems.length } };

  const applied = [
    ...(size ? [{ key: 'size', label: SIZE[size]?.label || size }] : []),
    ...(desc ? [{ key: 'desc', label: DESC[desc]?.label || desc }] : []),
  ];

  return (
    <>
    <ListScaffold
      title={desk || ff === 'tablet' ? '产品套装' : '产品资料'}
      total={desk ? undefined : list.total}
      subtitle={desk ? <>套装在下单时按用量规则展开成产品行。<b> 套装本身不记库存</b><span className={s.total}>共 {list.total} 个</span></> : undefined}
      sidebar={desk ? <ViewSidebar title="产品视图" views={sidebarViews} active={facet === 'all' ? 'bundles' : facet} onPick={(key) => set({ facet: key, id: undefined })} /> : undefined}
      segments={desk ? undefined : <Segments items={segments} />}
      chips={<ViewChips items={desk
        ? VIEWS.map((v) => ({ key: v.key, label: v.label, count: stats ? v.count(stats) : undefined }))
        : [
          { key: 'all', label: '全部', count: list.total },
          { key: 'shortage', label: '含缺货组件', count: list.hasNext ? undefined : facetCounts.shortage },
          { key: 'perGroup', label: '含每组用量', count: list.hasNext ? undefined : facetCounts.perGroup },
        ]} value={desk ? view.key : facet} onChange={(k) => desk ? set({ view: k === 'all' ? undefined : k, id: undefined }) : set({ facet: k, id: undefined })} />}
      filterBtn={desk || ff === 'phone' ? undefined : <FilterButton count={applied.length} onClick={() => setPanel(true)} />}
      applied={<AppliedChips items={applied} onRemove={(k) => set({ [k]: undefined })} onClear={() => set({ size: undefined, desc: undefined })} />}
      search={<SearchBox value={q} placeholder={desk ? '套装名称 / 编码' : '套装名称 / 编码 / 组件产品'} onSubmit={(v) => set({ q: v, id: undefined })} />}
      searching={!!q}
      create={can('products.bundle.write') ? { href: '/bundles/new', label: '新建套装', local: true } : null}
      actions={desk ? <button type="button" className={s.export} onClick={() => exportBundles(displayItems)} disabled={list.hasNext} title={list.hasNext ? '请缩小筛选范围后导出（当前最多读取 100 条）' : undefined}>导出</button> : undefined}
      list={
        <ListBody {...(desk ? deskCommon : common)}>
          <CardList bar={<><span>{facet === 'all' ? list.total : displayItems.length} 个 · 套装本身不记库存{q && ` · 搜索「${q}」`}</span><button type="button" className={s.mobileFitSort} aria-label={fitSort === 'asc' ? '可配齐套数升序' : fitSort === 'desc' ? '可配齐套数降序' : '按可配齐套数排序'} aria-pressed={fitSort !== null} onClick={() => setFitSort((current) => current === 'asc' ? 'desc' : 'asc')}>可配齐<Icon name="sort" size={14} /></button></>} hasNext={list.hasNext} loading={list.loading} count={mobileItems.length} sentinel={setSentinel}>
      {mobileItems.map((b) => (
              <BundleCard key={b.id} b={b} fit={fits.get(b.id)} selected={ff === 'tablet' && b.id === selectedId} to={ff === 'tablet' ? `/bundles?${withParam(sp, 'id', b.id)}` : `/bundles/${b.id}`} />
            ))}
          </CardList>
        </ListBody>
      }
      desk={
        <ListBody {...deskCommon}>
          <DataTable cols={cols} rows={displayItems} onRow={(b) => nav(`/bundles/${b.id}`)} minWidth={1100} />
          <p className={s.foot}>「可配齐套数」按当前可用库存算，红色表示不足 50 套。蓝底小块是按组计的组件。</p>
        </ListBody>
      }
      pager={desk && displayItems.length > 0 ? <Pager page={list.page} size={list.pageSize} count={displayItems.length} total={facet === 'all' ? list.total : displayItems.length} hasNext={list.hasNext} onGo={list.goto} /> : undefined}
      onRefresh={desk ? undefined : list.reload}
      detail={
        ff === 'tablet'
          ? selectedId
            ? <BundleDetail key={selectedId} id={selectedId} embedded />
            : <Empty icon="cards" title="选择左侧套装查看组成">组件用量与可配齐套数在这里打开。</Empty>
          : undefined
      }
      overlay={panel && (
        <FilterSheet
          value={{ size, desc }}
          groups={[
            { key: 'size', title: '组成规模', opts: Object.entries(SIZE).map(([value, o]) => ({ value, label: o.label, count: stats ? o.count(stats) : undefined })) },
            { key: 'desc', title: '说明', opts: Object.entries(DESC).map(([value, o]) => ({ value, label: o.label, count: stats ? o.count(stats) : undefined })) },
          ]}
          hint="「含缺货组件」「含按组用量」两个视图需要后端支持，暂不提供。"
          onClose={() => setPanel(false)}
          onApply={(v) => { setPanel(false); set({ size: v.size, desc: v.desc, id: undefined }); }}
        />
      )}
    />
      {deleteTarget && <div className={s.deleteBackdrop} onMouseDown={(event) => { if (event.target === event.currentTarget && !deleting) setDeleteTarget(null); }}>
        <section className={s.deleteDialog} role="dialog" aria-modal="true" aria-labelledby="bundle-delete-title">
          <header><h2 id="bundle-delete-title">删除套装「{deleteTarget.name}」？</h2><button type="button" aria-label="关闭" disabled={deleting} onClick={() => setDeleteTarget(null)}>×</button></header>
          <p>删除后，该套装将从 V1 和 V2 的套装列表及新订单选择中移除；历史订单明细会保留。</p>
          {deleteError && <p className={s.deleteError} role="alert">{deleteError}</p>}
          {import.meta.env.VITE_READONLY !== '0' && <p className={s.deleteReadonly}>当前为只读模式，删除暂不可提交。</p>}
          <footer><button type="button" disabled={deleting} onClick={() => setDeleteTarget(null)}>取消</button><button type="button" className={s.deleteConfirm} disabled={deleting || import.meta.env.VITE_READONLY !== '0'} onClick={() => void (async () => {
            setDeleting(true); setDeleteError('');
            try {
              await deleteBundle(deleteTarget.id);
              window.dispatchEvent(new Event('ims:v2:refresh'));
              setDeleteTarget(null);
              list.reload();
            } catch (cause) {
              const uncertain = !(cause instanceof ApiError) || cause.status === 0 || cause.status >= 500;
              setDeleteError(uncertain ? '操作结果未确认，请返回列表核对后再处理。' : cause instanceof Error ? cause.message : '删除失败');
            } finally { setDeleting(false); }
          })()}>{deleting ? '删除中…' : '确认删除'}</button></footer>
        </section>
      </div>}
    </>
  );
}
